import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  FileText, FileImage, Download, Loader2, AlertTriangle, Search, Building2, MapPin, User,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  listCompanyDocuments, listWorkplaceDocuments, downloadDocument,
  searchWorkers, getCompanyWorkers, searchCompanies, getWorkplaces,
} from '../services/api';
import { can, isSedeScoped, USER_TYPES } from '../lib/permissions';
import { useToast } from '../components/Toast';
import { FEEDBACK } from '../services/feedbackMessages';

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-ES', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const fileIcon = (filename) => {
  const ext = (filename || '').split('.').pop()?.toLowerCase();
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'heic'].includes(ext)) return FileImage;
  return FileText;
};

// `onError(err)`: quién avisa del fallo (el toast de la página).
const triggerDownload = async (doc, onError) => {
  try {
    const blob = await downloadDocument(doc.id);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.filename || `documento-${doc.id}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  } catch (err) {
    onError?.(err);
  }
};

const DocumentosPage = () => {
  const { user } = useAuth();
  const toast = useToast();
  const onDownloadError = (err) => toast.error(err, FEEDBACK.downloadFailed);
  const isMaster = user?.type === USER_TYPES.MECHAGODZILLA;
  const isInspector = user?.type === USER_TYPES.PUNISHER;
  const canSeeAll = can(user?.type, 'viewAllDocuments');
  const sedeScoped = isSedeScoped(user?.type);

  const [docs, setDocs] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [workplacesById, setWorkplacesById] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Trabajadores y empresas para enriquecer cada doc
      const [companiesData, workersData] = await Promise.all([
        searchCompanies('').catch(() => []),
        searchWorkers('').catch(() => []),
      ]);
      setCompanies(Array.isArray(companiesData) ? companiesData : []);
      setWorkers(Array.isArray(workersData) ? workersData : []);

      // Sedes de las empresas implicadas (para mostrar nombre, no id)
      const companyIdsForWp = canSeeAll
        ? (Array.isArray(companiesData) ? companiesData : []).map((c) => c.id)
        : (user?.companyId ? [user.companyId] : []);
      const wpLists = await Promise.all(companyIdsForWp.map((cid) =>
        getWorkplaces(cid).catch(() => [])));
      const wpMap = new Map();
      for (const arr of wpLists) {
        for (const wp of (Array.isArray(arr) ? arr : [])) wpMap.set(Number(wp.id), wp);
      }
      setWorkplacesById(wpMap);

      let collected = [];
      if (!canSeeAll) {
        // Cualquier rol que no sea inspector ni super (trabajador, supervisor,
        // admin empresa, admin sede): SÓLO ve los documentos que él mismo subió.
        // Cinturón de seguridad: si user.id no está hidratado, lista vacía —
        // jamás listamos los documentos del resto del equipo.
        const myId = Number(user?.id);
        if (Number.isFinite(myId) && user?.companyId) {
          const queries = [
            listCompanyDocuments(user.companyId).catch(() => []),
          ];
          if (user.workingSite) {
            queries.push(listWorkplaceDocuments(user.companyId, user.workingSite).catch(() => []));
          }
          const lists = await Promise.all(queries);
          const seen = new Set();
          for (const list of lists) {
            for (const d of (Array.isArray(list) ? list : [])) {
              if (Number(d.uploaderId) !== myId) continue;
              if (seen.has(d.id)) continue;
              seen.add(d.id);
              collected.push(d);
            }
          }
        }
      } else if (isMaster) {
        // Mechagodzilla: todos los documentos. Iteramos empresas.
        const lists = await Promise.all(
          (Array.isArray(companiesData) ? companiesData : []).map((c) =>
            listCompanyDocuments(c.id).catch(() => []))
        );
        collected = lists.flat();
      } else if (sedeScoped && user?.companyId && user?.workingSite) {
        // Admin sede / overseer: sólo su sede. Filtramos también en cliente
        // por si el backend devolviera más de la cuenta — cinturón de seguridad.
        const list = await listWorkplaceDocuments(user.companyId, user.workingSite).catch(() => []);
        const targetSite = String(user.workingSite);
        collected = (Array.isArray(list) ? list : []).filter(
          (d) => String(d.workplaceId ?? '') === targetSite
        );
      } else if (user?.companyId) {
        // Admin empresa / inspector con companyId: toda la empresa, todas las sedes.
        // Cinturón de seguridad: filtramos local por companyId también.
        const list = await listCompanyDocuments(user.companyId).catch(() => []);
        const targetCompany = Number(user.companyId);
        collected = (Array.isArray(list) ? list : []).filter(
          (d) => Number(d.companyId) === targetCompany
        );
      } else if (isInspector) {
        // Inspector sin companyId: ve todas (caso especial).
        const lists = await Promise.all(
          (Array.isArray(companiesData) ? companiesData : []).map((c) =>
            listCompanyDocuments(c.id).catch(() => []))
        );
        collected = lists.flat();
      }

      // Ordena más reciente primero
      collected.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      setDocs(collected);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [user?.id, user?.companyId, user?.workingSite, user?.type, canSeeAll, isMaster, isInspector, sedeScoped]);

  useEffect(() => { load(); }, [load]);

  const enriched = useMemo(() => docs.map((d) => {
    const uploader = workers.find((w) => w.id === d.uploaderId) || null;
    const company = companies.find((c) => c.id === d.companyId) || null;
    return { ...d, _uploader: uploader, _company: company };
  }), [docs, workers, companies]);

  const filtered = useMemo(() => {
    if (!query.trim()) return enriched;
    const q = query.toLowerCase();
    return enriched.filter((d) =>
      (d.filename || '').toLowerCase().includes(q) ||
      (d._uploader?.name || '').toLowerCase().includes(q) ||
      (d._company?.name || '').toLowerCase().includes(q)
    );
  }, [enriched, query]);

  return (
    <div className="h-[calc(100vh-64px-4rem)] flex flex-col min-h-0">
      <div className="flex items-end justify-between mb-5 gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold mb-1">Documentos</div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-ink">
            {canSeeAll ? 'Documentos del equipo' : 'Mis documentos'}
          </h1>
          <div className="text-xs text-ink-muted mt-1">
            {canSeeAll
              ? sedeScoped ? 'Justificantes y archivos subidos por trabajadores de tu sede.' : 'Justificantes y archivos subidos por trabajadores de tu ámbito.'
              : 'Justificantes y archivos que tú has subido (vacaciones, ausencias de día completo…).'}
          </div>
        </div>
        <div className="relative shrink-0">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por nombre o archivo"
            className="pl-9 pr-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm w-72"
          />
        </div>
      </div>

      {error && (
        <div className="border border-danger/30 bg-danger/5 rounded-xl p-3 mb-4 flex items-start gap-3">
          <AlertTriangle size={16} className="text-danger shrink-0 mt-0.5" />
          <div className="flex-1 text-sm font-semibold text-ink">{error.message}</div>
        </div>
      )}

      <div className="border-t border-slate-200 flex-1 min-h-0 overflow-y-auto">
        {loading ? (
          <div className="py-10 flex items-center justify-center gap-2 text-ink-muted">
            <Loader2 size={18} className="animate-spin text-primary" />
            <span className="text-sm">Cargando documentos…</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 flex flex-col items-center gap-2">
            <FileText size={28} className="text-ink-muted/50" />
            <div className="text-sm text-ink-muted">{docs.length === 0 ? 'No hay documentos todavía.' : 'Ningún archivo coincide con el filtro.'}</div>
          </div>
        ) : (
          <table className="w-full">
            <thead className="sticky top-0 bg-background">
              <tr className="text-xs uppercase tracking-wider text-ink-muted">
                <th className="text-left font-semibold py-3 pr-4">Archivo</th>
                {canSeeAll && <th className="text-left font-semibold py-3 pr-4 hidden md:table-cell">Trabajador</th>}
                {(isMaster || isInspector) && <th className="text-left font-semibold py-3 pr-4 hidden lg:table-cell">Empresa</th>}
                <th className="text-left font-semibold py-3 pr-4 hidden lg:table-cell">Sede</th>
                <th className="text-left font-semibold py-3 pr-4">Subido</th>
                <th className="text-right font-semibold py-3 pr-4 w-28"></th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {filtered.map((d) => {
                const Icon = fileIcon(d.filename);
                return (
                  <tr key={d.id} className="border-t border-slate-100 hover:bg-white transition-colors">
                    <td className="py-2.5 pr-4">
                      <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-lg bg-primary-soft flex items-center justify-center shrink-0">
                          <Icon size={16} className="text-primary-darker" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-ink truncate max-w-[240px]">{d.filename}</div>
                          <div className="text-[11px] text-ink-muted">#{d.id}</div>
                        </div>
                      </div>
                    </td>
                    {canSeeAll && (
                      <td className="py-2.5 pr-4 hidden md:table-cell">
                        {d._uploader ? (
                          <div className="inline-flex items-center gap-1.5 text-ink">
                            <User size={11} className="text-ink-muted" />
                            <span className="font-medium">{d._uploader.name}</span>
                          </div>
                        ) : (
                          <span className="text-ink-muted/60 italic">#{d.uploaderId}</span>
                        )}
                      </td>
                    )}
                    {(isMaster || isInspector) && (
                      <td className="py-2.5 pr-4 hidden lg:table-cell">
                        <div className="inline-flex items-center gap-1.5 text-ink-muted">
                          <Building2 size={11} />
                          <span className="text-xs">{d._company?.name || `#${d.companyId}`}</span>
                        </div>
                      </td>
                    )}
                    <td className="py-2.5 pr-4 hidden lg:table-cell">
                      {d.workplaceId ? (
                        <div className="inline-flex items-center gap-1.5 text-ink-muted">
                          <MapPin size={11} />
                          <span className="text-xs">{workplacesById.get(Number(d.workplaceId))?.name || `Sede #${d.workplaceId}`}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-ink-muted/60">—</span>
                      )}
                    </td>
                    <td className="py-2.5 pr-4 text-xs text-ink-muted tabular-nums">{fmtDate(d.createdAt)}</td>
                    <td className="py-2.5 pr-4 text-right">
                      <button
                        onClick={() => triggerDownload(d, onDownloadError)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-primary hover:bg-primary-soft transition-colors"
                      >
                        <Download size={13} /> Descargar
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default DocumentosPage;
